// =============================================================================
// CONNECT 2.0 — Active-page progress stepper: pure stage-model derivation.
// -----------------------------------------------------------------------------
// Replaces the dormant `proposal.progress`/`percentComplete` percent bar (never
// populated by order-service/connect-service) with a status-derived stepper for
// awarded products. This module is the FEATURE: it maps one product
// (`proposal` + `site` + that product's site-visit summary) onto an ordered list
// of lifecycle stages, each `complete | current | pending`, derived from the
// proposal `status`, the `atpSignedDate` timestamp, and the live
// `SiteVisitSchedule` state.
//
// The two site-visit stages read `SiteVisitSummary.status` — the schedule's
// state machine position, served on each `SiteDetail` by
// `GET /project/user-projects` — and NOTHING else. They no longer read
// `proposal.selectedSiteVisit`, and no longer treat a visit DATE as a stand-in
// for completion. Both signals were wrong, in different ways:
//
//  - `selectedSiteVisit` is written from exactly one place: order-service's
//    `saveSiteVisitTask` → `PUT /proposal/schedule-site-visit/order-tracker`.
//    That is the Order Tracker staff flow. CONNECT 2.0's own scheduling
//    negotiation writes a `SiteVisitSchedule` and never touches that field, so
//    every natively-scheduled visit read as never scheduled.
//  - the elapsed-date proxy carried no completion signal at all. A visit
//    explicitly marked `SITE_VISIT_COMPLETED` but dated in the FUTURE read as
//    incomplete — the reported defect (2026-08-29, `2608-A2677`: a completed
//    visit dated Sep 7 2026 with the product line stuck on "Schedule site
//    visit").
//
// Progress is otherwise monotonic: proposal status only moves forward, so the
// review-side stages never regress — a second revision cycle re-enters the same
// "Addressing comments" stage rather than jumping back from "Completed". The
// site-visit stages are the one place progress CAN move backwards, and it is
// honest when it does: a negotiation the site contact reopens
// (`REJECTED_ALTERNATIVES_PROVIDED`) or that is called off (`CANCELLED`)
// completes neither site-visit stage, so a row that read "Site visit" falls
// back to "Schedule site visit". The visit genuinely is unscheduled again; that
// is the state, not a display bug.
//
// Reference model: connect-ws's active lifecycle (`computeProject`), adapted to
// the signals we serialize on the Active list row. Framework-agnostic (types
// only) so the workspace can reuse it later — see the spec's "Workspace
// breadcrumb" deferred item.
// =============================================================================

import type { SiteVisitStatus } from '../../site-visit/site-visit-state.util';

export type StageKey =
  | 'authorized'
  | 'scheduleSiteVisit'
  | 'siteVisit'
  | 'report'
  | 'submitted'
  | 'addressingComments'
  | 'completed';

export type StageState = 'complete' | 'current' | 'pending';

export interface ProductStage {
  key: StageKey;
  label: string;
  state: StageState;
}

/**
 * The narrow site-visit projection that `GET /project/user-projects` attaches to
 * each `SiteDetail` (connect-service's `SiteVisitSummary`), or null/undefined
 * when the product has no `SiteVisitSchedule` at all. Deliberately NOT the full
 * schedule — it carries only what a list row, the dashboard KPI and the
 * workspace surfaces need, and every field here has a live consumer:
 *
 *  - `status` — this module, the dashboard KPI, both workspace surfaces.
 *  - `confirmedDate` — the KPI's "has it happened yet" check, and the date the
 *    workspace renders. UTC-noon-pinned, so it is zone-safe to compare and show.
 *
 * 🔴 `approvedDate` RENAMED to `confirmedDate`, following the wire.
 * `ProjectHandler` builds this summary as
 * `new SiteVisitSummary(schedule.getStatus(), schedule.getConfirmedDate())`, so
 * the old key stopped arriving when the persisted field was renamed. Nothing
 * caught it — every key here is optional and no build step checks this interface
 * against the JSON — so the KPI's "has it happened yet" test read `undefined` and
 * counted nothing, and the workspace's field-work date rendered blank.
 *
 * 🔴 `timeWindow` DROPPED (Task 10) along with the AM/PM window it labelled.
 * The workspace Details row now renders the date alone — see
 * `workspace.component.ts`'s `buildSiteVisitSummary`.
 *
 * 🔴 `confirmedTime` IS DELIBERATELY ABSENT, and re-adding it alone is a bug.
 * The exact arrival time is a wall-clock string that means nothing without the
 * PROPERTY's zone, and this summary does not carry `siteTimeZone`. Rendering one
 * against the viewer's zone is a live bug class here with existing instances. It
 * was dropped rather than left unread precisely so nobody finds it on the DTO and
 * renders it: if a surface ever needs an arrival time, add `confirmedTime` AND
 * `siteTimeZone` together, as one deliberate change.
 *
 * `siteNumber` was dropped for a duller reason — the `SiteDetail` this hangs off
 * already carries `site.siteNumber`, and the two cannot disagree.
 */
export interface SiteVisitSummary {
  status?: SiteVisitStatus | string | null;
  confirmedDate?: string | Date | null;
}

export interface ProductProgress {
  /** Applicable stages only, in lifecycle order. */
  stages: ProductStage[];
  /** Label of the current stage, or null when every applicable stage is complete. */
  currentLabel: string | null;
  /** round(completed / applicable * 100). */
  percent: number;
  /** Number of applicable stages (5 without a site visit, else 7). */
  applicableCount: number;
}

const STAGE_LABELS: Record<StageKey, string> = {
  authorized: 'Authorized',
  scheduleSiteVisit: 'Schedule site visit',
  siteVisit: 'Site visit',
  report: 'Report',
  submitted: 'Submitted for review',
  addressingComments: 'Addressing comments',
  completed: 'Completed',
};

// The two site-visit stages are conditional — included only when the product
// needs a visit (see resolveProductProgress).
const SITE_VISIT_KEYS = new Set<StageKey>(['scheduleSiteVisit', 'siteVisit']);

const ORDER: StageKey[] = [
  'authorized',
  'scheduleSiteVisit',
  'siteVisit',
  'report',
  'submitted',
  'addressingComments',
  'completed',
];

// The site-visit statuses at which "Schedule site visit" is behind us: a day
// everyone has agreed to exists, whatever is left to pin down about the arrival
// time on it.
//
// Written out as an explicit SET, deliberately never an ordinal comparison
// against `SiteVisitStatusEnum`'s declaration order.
// `REJECTED_ALTERNATIVES_PROVIDED` sits between `TIME_CONFIRMED` and
// `SITE_VISIT_COMPLETED` in that enum and is NOT a completion — it means the
// site contact rejected the approved window and countered, i.e. the day is open
// again. `CANCELLED` is declared last and is not a completion either. An
// index/ordinal test would silently call both of them done.
//
// Why the bar is APPROVED rather than TIME_CONFIRMED: the cut-over migration
// lands staff-confirmed visits at APPROVED and deliberately makes them
// indistinguishable from natively-approved rows (`SiteVisitMigrationHandler` —
// "APPROVED with no proposed day would not match what APPROVE_WINDOW
// produces"). `confirmedDate` is stamped on both paths, so it cannot
// discriminate either; drawing the line at TIME_CONFIRMED would strand every
// migrated visit on "Schedule site visit" for good.
//
// Exported because the dashboard's `upcomingSiteVisits` KPI needs the same set
// (an upcoming visit is one in this set that is not yet `SITE_VISIT_COMPLETED`
// and whose date has not passed). One definition, two readers — restating it in
// the second reader is exactly how the two drift apart.
export const SITE_VISIT_SCHEDULED_STATUSES: ReadonlySet<SiteVisitStatus> =
  new Set<SiteVisitStatus>([
    'APPROVED',
    'PENDING_TIME_PROPOSAL',
    'PENDING_TIME_CONFIRMATION',
    'TIME_CONFIRMED',
    'SITE_VISIT_COMPLETED',
  ]);

// Statuses at which the "Report" stage (and everything before it) is behind us.
// ADDRESSING_COMMENTS is handled defensively: connect-service emits REVISION_*
// for the addressing-comments phase, but the pill map still lists this
// connect-ws-style status, so keep the two aligned.
const PAST_ACTIVE = new Set<string>([
  'REVIEW_PENDING',
  'IN_REVIEW',
  'REVISION_REQUESTED',
  'REVISION_PENDING',
  'ADDRESSING_COMMENTS',
  'COMPLETED',
]);

// Statuses at which the "Submitted for review" stage is behind us. The revision
// statuses (and the defensive ADDRESSING_COMMENTS) collapse the
// review→revision→resubmit round-trip into the single "Addressing comments"
// stage, so all count review as complete.
const PAST_REVIEW = new Set<string>([
  'REVISION_REQUESTED',
  'REVISION_PENDING',
  'ADDRESSING_COMMENTS',
  'COMPLETED',
]);

/**
 * Map one product (`proposal` + `site` + `siteVisit`) onto its ordered progress
 * stages.
 *
 * Null-safe on every argument. `current` is the FIRST incomplete applicable
 * stage; everything before it renders `complete`, everything after it `pending`.
 *
 * Site-visit progression, driven entirely by `siteVisit.status`:
 *  - `scheduleSiteVisit` completes once the status reaches
 *    {@link SITE_VISIT_SCHEDULED_STATUSES} — an agreed day exists.
 *  - `siteVisit` completes only at `SITE_VISIT_COMPLETED`. That is an explicit
 *    signal written by the `COMPLETE` transition; no date is consulted, so a
 *    completed visit reads complete whether its date is in the past or, as in
 *    the reported case, the future.
 *  Both are also gated behind `PAST_ACTIVE`, so a product already in review
 *  reads them complete regardless of what the schedule says — including the
 *  legacy rows that have no schedule at all.
 */
export function resolveProductProgress(
  proposal: any,
  site: any,
  siteVisit?: SiteVisitSummary | null
): ProductProgress {
  const status = String(proposal?.status ?? '').toUpperCase();
  const isCompleted = status === 'COMPLETED';
  const pastActive = PAST_ACTIVE.has(status);

  const atpSigned = proposal?.atpSignedDate != null;

  // The one site-visit input. Upper-cased defensively: the server serializes the
  // enum name, but a lower-cased value must not silently miss the set and read
  // as "never scheduled" — that is the class of bug this rewrite exists to fix.
  const visitStatus =
    siteVisit?.status == null ? null : String(siteVisit.status).toUpperCase();
  const visitScheduled =
    visitStatus !== null &&
    SITE_VISIT_SCHEDULED_STATUSES.has(visitStatus as SiteVisitStatus);
  const visitCompleted = visitStatus === 'SITE_VISIT_COMPLETED';

  // "Needs a visit" is `site.siteVisitRequired`, and nothing else. Nothing
  // site-visit-related renders for a product that does not require one — the
  // existence of a schedule does not override the flag, whatever status that
  // schedule carries. Visit creation is not gated on `siteVisitRequired` in
  // connect-service, so a stray schedule on a site flagged false is a reachable
  // shape; it is simply not something to surface.
  //
  // This is the rule `scheduling-card.component.ts` has always applied (see its
  // `requiringProducts`/`requiringCount` filter on `site.siteVisitRequired`).
  // Keying the stepper off the same flag makes the two agree instead of one
  // screen showing site-visit stages the other refuses to show a card for.
  const visitRequired = site?.siteVisitRequired === true;

  // Applicable stages: drop BOTH site-visit stages when the product needs none.
  const applicableKeys: StageKey[] = ORDER.filter(
    (key) => !SITE_VISIT_KEYS.has(key) || visitRequired
  );

  // Per-stage completion. COMPLETED (⊂ PAST_ACTIVE / PAST_REVIEW) folds into the
  // status sets so a completed product always reads as fully complete.
  const isStageComplete = (key: StageKey): boolean => {
    switch (key) {
      case 'authorized':
        return atpSigned || isCompleted;
      case 'scheduleSiteVisit':
        return visitScheduled || pastActive;
      case 'siteVisit':
        return visitCompleted || pastActive;
      case 'report':
        return pastActive;
      case 'submitted':
        return PAST_REVIEW.has(status);
      case 'addressingComments':
        return isCompleted;
      case 'completed':
        return isCompleted;
    }
  };

  // current = first incomplete applicable stage. Deriving states from this index
  // (rather than the raw per-stage booleans) guarantees a contiguous, monotonic
  // display even for degenerate data where a later stage's condition is met
  // while an earlier one is not.
  const currentIndex = applicableKeys.findIndex((key) => !isStageComplete(key));

  const stages: ProductStage[] = applicableKeys.map((key, index) => {
    let state: StageState;
    if (currentIndex === -1 || index < currentIndex) {
      state = 'complete';
    } else if (index === currentIndex) {
      state = 'current';
    } else {
      state = 'pending';
    }
    return { key, label: STAGE_LABELS[key], state };
  });

  const applicableCount = applicableKeys.length;
  const completedCount = currentIndex === -1 ? applicableCount : currentIndex;
  const percent = Math.round((completedCount / applicableCount) * 100);
  const currentLabel = currentIndex === -1 ? null : STAGE_LABELS[applicableKeys[currentIndex]];

  return { stages, currentLabel, percent, applicableCount };
}
